import { Colors } from '@amelio/ui-library';

import type { ColorType } from '@amelio/ui-library';

interface ProficiencyLevelColorArgs {
    iconColor?: string | null;
    levelIndex: number;
}

interface RankedProficiencyLevel {
    id?: string;
    iconColor?: string | null;
}

interface RankedProficiencyLevelArgs {
    level?: RankedProficiencyLevel;
    orderedLevelIds: (string | undefined)[];
}

const POSITIONAL_FALLBACK_COLORS: ColorType[] = [
    Colors.SUCCESS_800,
    Colors.INFO,
    Colors.WARNING,
    Colors.ERROR,
    Colors.PRIMARY,
    Colors.SECONDARY,
];

const UNRANKED_LEVEL_COLOR = Colors.BLACK;

const TAILWIND_ARBITRARY_TEXT_COLOR_REGEX = /^text-\[(.+)\]$/;
const TAILWIND_TOKEN_TEXT_COLOR_REGEX = /^text-([a-z][a-z0-9-]*)$/;

const toCssColor = (color?: string | null): string | undefined => {
    if (!color) {
        return undefined;
    }

    const arbitraryColor = color.match(TAILWIND_ARBITRARY_TEXT_COLOR_REGEX)?.[1];
    if (arbitraryColor) {
        return arbitraryColor;
    }

    const colorToken = color.match(TAILWIND_TOKEN_TEXT_COLOR_REGEX)?.[1];
    if (colorToken) {
        return `var(--color-${colorToken})`;
    }

    return color;
};

const getPositionalFallbackColor = (levelIndex: number): ColorType => {
    if (levelIndex < 0) {
        return UNRANKED_LEVEL_COLOR;
    }

    return POSITIONAL_FALLBACK_COLORS[levelIndex % POSITIONAL_FALLBACK_COLORS.length];
};

const resolveProficiencyLevelColor = ({
    iconColor,
    levelIndex,
}: ProficiencyLevelColorArgs): string =>
    toCssColor(iconColor) ?? getPositionalFallbackColor(levelIndex);

const getProficiencyLevelRank = ({ level, orderedLevelIds }: RankedProficiencyLevelArgs) =>
    level?.id ? orderedLevelIds.indexOf(level.id) : -1;

const resolveRankedProficiencyLevelColor = ({
    level,
    orderedLevelIds,
}: RankedProficiencyLevelArgs): string =>
    resolveProficiencyLevelColor({
        iconColor: level?.iconColor,
        levelIndex: getProficiencyLevelRank({ level, orderedLevelIds }),
    });

export {
    getProficiencyLevelRank,
    resolveProficiencyLevelColor,
    resolveRankedProficiencyLevelColor,
};
